import { useSession } from './useSession';
import type { UseQueryOptions } from '@tanstack/react-query';

export type ConfigurationType = 'default' | 'no-cache' | 'cached' | 'short-cache';

function getQueryConfiguration(type: ConfigurationType): UseQueryOptions {
  switch (type) {
    case 'no-cache':
      return {
        staleTime: 0,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false,
      } as const;
    case 'cached':
      return {
        staleTime: 1000 * 60 * 10, // 10 minutes until refresh stale data
        cacheTime: 1000 * 60 * 30, // 30 minutes until dump cached data
        refetchOnWindowFocus: false, // Refetch if data is stale
        refetchIntervalInBackground: false, // Refetch if data is stale
      } as const;
    case 'short-cache':
      return {
        staleTime: 1000 * 30, // 30 seconds until refresh stale data
        cacheTime: 1000 * 60 * 2, // 2 minutes until dump cached data
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false,
      } as const;
    default:
      return {
        staleTime: 1000 * 60 * 10, // 10 minutes until refresh stale data
        cacheTime: 1000 * 60 * 30, // 30 minutes until dump cached data
        refetchOnWindowFocus: false, // Refetch if data is stale
        refetchIntervalInBackground: false, // Refetch if data is stale
        refetchOnMount: 'always', // Always refetch on mount until we invalidate properly
      } as const;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type QueryFunction = {
  queryKey: string;
  fn: (...args: any[]) => any;
  configurationType: ConfigurationType;
};

export function queryCreator<T extends QueryFunction, R = Awaited<ReturnType<T['fn']>>>(
  sessionPartnerUrl: string,
  queryFunction: T,
  settings: {
    /**
     * Function arguments, also used as query keys.
     */
    args: Parameters<T['fn']>;
    /**
     * Override the configuration type for the query.
     * If not provided, the configuration type from the query function will be used.
     */
    configurationTypeOverride?: ConfigurationType;
  },
): {
  queryKey: string[];
  queryFn: () => Promise<R>;
} {
  return {
    ...getQueryConfiguration(settings.configurationTypeOverride ?? queryFunction.configurationType),
    queryKey: [sessionPartnerUrl, queryFunction.queryKey, ...settings.args],
    queryFn: () => queryFunction.fn(...settings.args),
  };
}

export function useQueryCreator<T extends QueryFunction, R = Awaited<ReturnType<T['fn']>>>(
  queryFunction: T,
  settings: {
    /**
     * Function arguments, also used as query keys.
     */
    args: Parameters<T['fn']>;
    /**
     * Override the configuration type for the query.
     * If not provided, the configuration type from the query function will be used.
     */
    configurationTypeOverride?: ConfigurationType;
  },
): {
  queryKey: string[];
  queryFn: () => Promise<R>;
} {
  const { sessionPartnerUrl } = useSession();
  return queryCreator(sessionPartnerUrl, queryFunction, settings);
}

export function arrayToMap<T, K extends keyof T>(array: T[], key: K) {
  return array.reduce(
    (acc, curr) => {
      if (curr[key]) {
        acc[curr[key] as string] = curr;
      }
      return acc;
    },
    {} as Record<string, T>,
  );
}

export function arrayToMapTransformed<T, K extends keyof T, Transform>(
  array: T[],
  key: K,
  transform: (T: T) => Transform,
) {
  return array.reduce(
    (acc, curr) => {
      acc[curr[key] as string] = transform(curr);
      return acc;
    },
    {} as Record<string, Transform>,
  );
}
